import { fetchRest, getApiBaseUrl } from "@/config/api";
import type {
  PrestashopCatalogPage,
  PrestashopConnection,
  PrestashopImageOverride,
  PrestashopMelOverrides,
  PrestashopProduct,
} from "@/types/prestashop";

const baseUrl = () => `${getApiBaseUrl()}/prestashop`;

async function readJson<T>(response: Response): Promise<T> {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const rawMessage = (data as { message?: unknown }).message;
    const message = Array.isArray(rawMessage)
      ? rawMessage.join(", ")
      : String(rawMessage || `Erreur HTTP ${response.status}`);
    throw new Error(message);
  }
  return data as T;
}

export async function getPrestashopConnection() {
  const response = await fetchRest(`${baseUrl()}/connection`);
  return readJson<PrestashopConnection>(response);
}

export async function connectPrestashop(shopUrl: string, apiKey: string) {
  const response = await fetchRest(`${baseUrl()}/connection`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ shopUrl, apiKey }),
  });
  return readJson<PrestashopConnection>(response);
}

export async function disconnectPrestashop() {
  const response = await fetchRest(`${baseUrl()}/connection`, {
    method: "DELETE",
  });
  return readJson<{ success: boolean }>(response);
}

export type PrestashopMappingType =
  | "color"
  | "category"
  | "composition"
  | "brand";

export interface PrestashopSettings {
  defaultGender: "femme" | "homme" | "enfant" | "aucun" | null;
  defaultProvenanceId: number | null;
  priceAdjustmentPercent: number;
  priceIsUnit: boolean;
  weightIsUnit: boolean;
  defaultDeclinaisonId: number | null;
  defaultPackId: number | null;
  defaultSoldBy: "couleurs" | "melangees" | "tailles" | null;
  setupCompleted: boolean;
}

export type PrestashopOptionGroupRoleValue = "color" | "size" | "ignore";

export interface PrestashopOptionGroupRole {
  optionGroupId: number;
  sourceLabel: string;
  inferredRole: PrestashopOptionGroupRoleValue;
  role: PrestashopOptionGroupRoleValue;
  configured: boolean;
}

export interface PrestashopMapping {
  sourceLabel: string;
  sourceLabelNormalized: string;
  targetId: number;
  targetLabel: string | null;
}

export interface PrestashopMappingTarget {
  id: number;
  label: string;
  rootId: number | null;
  isDefault?: boolean;
}

export interface PrestashopPendingMappings {
  color: string[];
  colorHexByName: Record<string, string>;
  category: string[];
  categorySources: Array<{ sourceId: number; sourceLabel: string }>;
  composition: string[];
  brand: string[];
  scannedProducts: number;
  incomplete: boolean;
}

export async function getPrestashopPendingMappings() {
  return readJson<PrestashopPendingMappings>(
    await fetchRest(`${baseUrl()}/mappings/pending`)
  );
}

export async function createPrestashopBrandMapping(
  sourceLabel: string,
  brandName: string
) {
  return readJson<PrestashopMapping & { createdBrand: boolean }>(
    await fetchRest(`${baseUrl()}/mappings/brand/create-target`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sourceLabel, brandName }),
    })
  );
}

export async function getPrestashopSettings() {
  return readJson<PrestashopSettings>(await fetchRest(`${baseUrl()}/settings`));
}

export async function getPrestashopProvenances() {
  return readJson<Array<{ id: number; label: string }>>(
    await fetchRest(`${baseUrl()}/settings/provenances`)
  );
}

export interface PrestashopDefaultDeclinaison {
  id: number;
  titre: string;
  tailles: string;
  sizeCount: number;
}

export interface PrestashopDefaultPack {
  id: number;
  titre: string;
  quantities: string;
  sizeCount: number;
}

export async function getPrestashopDefaultDeclinaisons() {
  return readJson<PrestashopDefaultDeclinaison[]>(
    await fetchRest(`${baseUrl()}/settings/declinaisons`)
  );
}

export async function getPrestashopDefaultPacks() {
  return readJson<PrestashopDefaultPack[]>(
    await fetchRest(`${baseUrl()}/settings/packs`)
  );
}

export async function ensurePrestashopUnitDefaults() {
  return readJson<{ defaultDeclinaisonId: number; defaultPackId: number }>(
    await fetchRest(`${baseUrl()}/settings/unit-defaults`, {
      method: "POST",
    })
  );
}

export async function getPrestashopOptionGroupRoles() {
  return readJson<PrestashopOptionGroupRole[]>(
    await fetchRest(`${baseUrl()}/settings/option-groups`)
  );
}

export async function savePrestashopOptionGroupRole(
  optionGroupId: number,
  role: PrestashopOptionGroupRoleValue
) {
  return readJson<PrestashopOptionGroupRole[]>(
    await fetchRest(`${baseUrl()}/settings/option-groups/${optionGroupId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ role }),
    })
  );
}

export async function savePrestashopSettings(
  defaultGender: NonNullable<PrestashopSettings["defaultGender"]>,
  defaultProvenanceId: number,
  priceAdjustmentPercent: number,
  priceIsUnit: boolean,
  weightIsUnit: boolean,
  defaultDeclinaisonId: number | null,
  defaultPackId: number | null,
  defaultSoldBy: NonNullable<PrestashopSettings["defaultSoldBy"]>
) {
  return readJson<PrestashopSettings>(
    await fetchRest(`${baseUrl()}/settings`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        defaultGender,
        defaultProvenanceId,
        priceAdjustmentPercent,
        priceIsUnit,
        weightIsUnit,
        defaultDeclinaisonId,
        defaultPackId,
        defaultSoldBy,
      }),
    })
  );
}

export interface PrestashopCategoryWeight {
  categoryId: number;
  categoryLabel: string;
  weightKg: number;
}

export async function getPrestashopCategoryWeights() {
  return readJson<PrestashopCategoryWeight[]>(
    await fetchRest(`${baseUrl()}/category-weights`)
  );
}

export async function savePrestashopCategoryWeight(
  categoryId: number,
  weightKg: number
) {
  return readJson<PrestashopCategoryWeight[]>(
    await fetchRest(`${baseUrl()}/category-weights`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ categoryId, weightKg }),
    })
  );
}

export async function deletePrestashopCategoryWeight(categoryId: number) {
  return readJson<PrestashopCategoryWeight[]>(
    await fetchRest(`${baseUrl()}/category-weights`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ categoryId }),
    })
  );
}

export async function getPrestashopMappings(type: PrestashopMappingType) {
  return readJson<PrestashopMapping[]>(
    await fetchRest(`${baseUrl()}/mappings/${type}`)
  );
}

export async function getPrestashopMappingTargets(type: PrestashopMappingType) {
  return readJson<PrestashopMappingTarget[]>(
    await fetchRest(`${baseUrl()}/mappings/${type}/targets`)
  );
}

export async function savePrestashopMapping(
  type: PrestashopMappingType,
  sourceLabel: string,
  targetId: number
) {
  return readJson<PrestashopMapping>(
    await fetchRest(`${baseUrl()}/mappings/${type}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sourceLabel, targetId }),
    })
  );
}

export async function resetAllPrestashopProductOverrides() {
  const response = await fetchRest(`${baseUrl()}/overrides`, {
    method: "DELETE",
  });
  return readJson<{ success: boolean; cleared: number }>(response);
}

export async function deletePrestashopMapping(
  type: PrestashopMappingType,
  sourceLabel: string
) {
  return readJson<{ success: boolean; updatedProducts: number }>(
    await fetchRest(`${baseUrl()}/mappings/${type}`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sourceLabel }),
    })
  );
}

export async function getPrestashopProducts(
  page: number,
  limit: number,
  search?: string
) {
  const query = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });
  const trimmedSearch = search?.trim();
  if (trimmedSearch) query.set("search", trimmedSearch);
  const response = await fetchRest(`${baseUrl()}/products?${query.toString()}`);
  return readJson<PrestashopCatalogPage>(response);
}

export async function getPrestashopProductVariantStocks(productId: number) {
  const response = await fetchRest(
    `${baseUrl()}/products/${productId}/variant-stocks`
  );
  return readJson<{
    stocks: Array<{
      combinationId: number;
      quantity: number;
      optionValueIds: number[];
      colorName: string | null;
      sizeName: string | null;
      imageIds?: number[];
    }>;
  }>(response);
}

export interface PrestashopSyncEvent {
  id: number;
  prestashopProductId: number;
  efashionProductId: number;
  status: "pending" | "processing" | "failed";
  attempts: number;
  lastError: string | null;
  nextAttemptAt: string | null;
  updatedAt: string;
}

export async function getPrestashopSyncEvents() {
  return readJson<PrestashopSyncEvent[]>(
    await fetchRest(`${baseUrl()}/sync-events`)
  );
}

export async function retryPrestashopSyncEvent(eventId: number) {
  return readJson<{ success: boolean }>(
    await fetchRest(`${baseUrl()}/sync-events/${eventId}/retry`, {
      method: "POST",
    })
  );
}

export async function savePrestashopProductOverrides(
  productId: number,
  overrides: PrestashopMelOverrides
) {
  const response = await fetchRest(
    `${baseUrl()}/products/${productId}/overrides`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(overrides),
    }
  );
  return readJson<{ success: boolean; overrides: PrestashopMelOverrides }>(
    response
  );
}

export type PrestashopBulkOverridesReport = {
  updated: number;
  failed: number;
  results: Array<{
    prestashopProductId: number;
    success: boolean;
    message?: string;
  }>;
};

export async function savePrestashopBulkOverrides(
  productIds: number[],
  overrides: PrestashopMelOverrides
) {
  const response = await fetchRest(`${baseUrl()}/products/overrides/bulk`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ productIds, overrides }),
  });
  return readJson<PrestashopBulkOverridesReport>(response);
}

export const PRESTASHOP_BULK_BATCH_SIZE = 50;

export async function savePrestashopBulkOverridesInBatches(
  productIds: number[],
  overrides: PrestashopMelOverrides,
  options?: {
    batchSize?: number;
    onBatchProgress?: (progress: {
      batchIndex: number;
      batchCount: number;
      batchUpdated: number;
      batchFailed: number;
      updatedSoFar: number;
      failedSoFar: number;
    }) => void;
  }
): Promise<PrestashopBulkOverridesReport> {
  const uniqueIds = Array.from(
    new Set(
      productIds
        .map((id) => Number(id))
        .filter((id) => Number.isInteger(id) && id > 0)
    )
  );
  const batchSize = Math.max(
    1,
    options?.batchSize ?? PRESTASHOP_BULK_BATCH_SIZE
  );
  const chunks = chunkProductIds(uniqueIds, batchSize);
  const aggregated: PrestashopBulkOverridesReport = {
    updated: 0,
    failed: 0,
    results: [],
  };

  for (let index = 0; index < chunks.length; index += 1) {
    const report = await savePrestashopBulkOverrides(chunks[index], overrides);
    aggregated.updated += report.updated;
    aggregated.failed += report.failed;
    aggregated.results.push(...report.results);
    options?.onBatchProgress?.({
      batchIndex: index + 1,
      batchCount: chunks.length,
      batchUpdated: report.updated,
      batchFailed: report.failed,
      updatedSoFar: aggregated.updated,
      failedSoFar: aggregated.failed,
    });
  }

  return aggregated;
}

export async function getPrestashopProductImage(
  productId: number,
  imageId: number
): Promise<Blob> {
  const response = await fetchRest(
    `${baseUrl()}/products/${productId}/images/${imageId}`
  );
  if (!response.ok) {
    throw new Error(`Image PrestaShop indisponible (${response.status}).`);
  }
  return response.blob();
}

export function getPrestashopCustomImageUrl(
  productId: number,
  filename: string
): string {
  return `${baseUrl()}/products/${productId}/images/custom/${encodeURIComponent(
    filename
  )}`;
}

export async function uploadPrestashopCustomImage(
  productId: number,
  file: File
): Promise<{ file: string; url: string }> {
  const body = new FormData();
  body.append("photo", file);
  const response = await fetchRest(
    `${baseUrl()}/products/${productId}/images/upload`,
    {
      method: "POST",
      body,
    }
  );
  return readJson<{ file: string; url: string }>(response);
}

export async function savePrestashopProductImages(
  productId: number,
  images: PrestashopImageOverride[]
) {
  const response = await fetchRest(
    `${baseUrl()}/products/${productId}/images`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ images }),
    }
  );
  return readJson<{ success: boolean; images: PrestashopImageOverride[] }>(
    response
  );
}

export interface PrestashopImportRow {
  id: number;
  reference: string;
  name: string;
  active: boolean;
  price: number;
  weight: number;
  imageIds: number[];
  description?: string;
  descriptionShort?: string;
  descriptions?: PrestashopProduct["descriptions"];
  dateUpdated?: string | null;
  overrides: PrestashopMelOverrides;
  brandName?: string;
}

/** La mise en ligne envoie la ligne du tableau, pas seulement l’identifiant. */
export function toPrestashopImportRow(
  product: PrestashopProduct
): PrestashopImportRow {
  return {
    id: product.id,
    reference: product.reference,
    name: product.name,
    active: product.active,
    price: product.price,
    weight: product.weight,
    imageIds: product.imageIds ?? [],
    description: product.description,
    descriptionShort: product.descriptionShort,
    descriptions: product.descriptions,
    dateUpdated: product.dateUpdated,
    overrides: product.overrides ?? {},
    brandName: product.brandName,
  };
}

export async function importPrestashopProducts(
  products: PrestashopImportRow[]
) {
  const response = await fetchRest(`${baseUrl()}/products/import`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ products }),
  });
  return readJson<PrestashopImportReport>(response);
}

export const PRESTASHOP_IMPORT_BATCH_SIZE = 20;

export type PrestashopImportReport = {
  imported: number;
  failed: number;
  results: Array<{
    prestashopProductId: number;
    success: boolean;
    efashionProductId?: number;
    published?: boolean;
    visibility?: "online" | "offline" | "draft";
    message?: string;
  }>;
};

export function chunkProductIds(
  productIds: number[],
  batchSize: number
): number[][] {
  const chunks: number[][] = [];
  for (let index = 0; index < productIds.length; index += batchSize) {
    chunks.push(productIds.slice(index, index + batchSize));
  }
  return chunks;
}

export async function importPrestashopProductsInBatches(
  products: PrestashopImportRow[],
  options?: {
    batchSize?: number;
    onBatchProgress?: (progress: {
      batchIndex: number;
      batchCount: number;
      batchImported: number;
      batchFailed: number;
      importedSoFar: number;
      failedSoFar: number;
      results: PrestashopImportReport["results"];
    }) => void;
  }
): Promise<PrestashopImportReport> {
  const uniqueRows: PrestashopImportRow[] = [];
  const seen = new Set<number>();
  for (const product of products) {
    const id = Number(product?.id);
    if (!Number.isInteger(id) || id <= 0 || seen.has(id)) continue;
    seen.add(id);
    uniqueRows.push(product);
  }
  const batchSize = Math.max(
    1,
    options?.batchSize ?? PRESTASHOP_IMPORT_BATCH_SIZE
  );
  const chunks: PrestashopImportRow[][] = [];
  for (let index = 0; index < uniqueRows.length; index += batchSize) {
    chunks.push(uniqueRows.slice(index, index + batchSize));
  }
  const aggregated: PrestashopImportReport = {
    imported: 0,
    failed: 0,
    results: [],
  };

  for (let index = 0; index < chunks.length; index += 1) {
    const report = await importPrestashopProducts(chunks[index]);
    aggregated.imported += report.imported;
    aggregated.failed += report.failed;
    aggregated.results.push(...report.results);
    options?.onBatchProgress?.({
      batchIndex: index + 1,
      batchCount: chunks.length,
      batchImported: report.imported,
      batchFailed: report.failed,
      importedSoFar: aggregated.imported,
      failedSoFar: aggregated.failed,
      results: report.results,
    });
  }

  return aggregated;
}
